import { SectionCard } from '@atlas/ui-components'
import { Skeleton } from '@atlas/ui-primitives'
import type { ProductPage } from '@/domain/explore.types'

/** Shared by a real row and its skeleton, so the placeholder rows sit on the same rhythm. */
const ROW_CLASS = 'flex items-baseline gap-3 border-b border-ds-border py-2.5 last:border-0 last:pb-0'

/** The scope column: fixed so the names below each other line up as one column. */
const SCOPE_CLASS = 'w-32 shrink-0 font-mono text-ds-2xs uppercase tracking-ds-wider text-ds-text-subtle'

/** The card's heading. Shared with the page's skeleton so the two cannot diverge. */
export const PRODUCT_NAMING_TITLE = 'Naming'

/**
 * The card's body while the page read runs: three rows on the same rhythm a real one sits
 * on, a scope over its name.
 *
 * Exported so the page's own first-read skeleton stands the card in the same way, rather
 * than as generic text lines that then reflow into rows.
 */
export function ProductNamingBodySkeleton({ label }: { label?: string }) {
	return (
		<>
			{label ? <span className="sr-only">{label}</span> : null}
			<ul aria-busy="true">
				{Array.from({ length: 3 }, (_, i) => (
					<li key={i} className={ROW_CLASS}>
						<Skeleton shape="text" className="h-3 w-24 shrink-0" />
						<Skeleton shape="text" className="w-1/2" />
					</li>
				))}
			</ul>
		</>
	)
}

export interface ProductNamingCardProps {
	/**
	 * The naming surface as the read projects it: ordered general to specific, each scope
	 * already carrying its code and label.
	 */
	names: ProductPage['names'] | undefined
	/**
	 * What the card says when nothing is recorded. Defaults to the product wording; the brand
	 * page passes its own, because "this product" is wrong on a brand.
	 */
	emptyLabel?: string
	/** The subject named in the loading and error copy — "product" unless told otherwise. */
	subject?: string
	/**
	 * The subject's OWN name — the one it goes by wherever no override applies.
	 *
	 * Listed as the baseline row so the overrides read as differences FROM something. Without it a
	 * reader sees "EMEA → FINADYNE®" and cannot tell what the brand is called anywhere else, which
	 * is the more useful half of the fact.
	 */
	defaultName?: string
	isLoading?: boolean
	/**
	 * Whether the page read failed. Distinct from an empty projection: "no names are
	 * recorded" is a gap a reader acts on, and reporting it for a read that never landed
	 * would state that a product has one name when the service simply could not say.
	 */
	isError?: boolean
}

/**
 * Where the subject is called something other than its own name, one row per geography
 * scope. A market absent from this list is one it goes by its own name in.
 *
 * Serves the product page and the brand page: a brand's names are region-split more often
 * than a product's (Banamine/Finadyne), and the projection shape is identical, so the card
 * takes its subject wording as props rather than being cloned per page.
 *
 * Renders the projection verbatim and derives NOTHING — neither the order nor the scope
 * labels, both of which are the read's to decide: a scope is held as a database id this page
 * cannot resolve.
 *
 * Deliberately does not single out the row the title came from. The title band states the
 * name in force, so marking it here would say the same thing twice, and the mark cost a
 * column of its own on every row that did not have it.
 *
 * The page's filter rail deliberately does not reach this card. The rail narrows what the
 * page LISTS; a product's names are not content, and a species filter emptying this table
 * would state something untrue about the product.
 *
 * Read-only: mappings are authored in the admin mapping grid, which owns that write.
 */
export function ProductNamingCard({
	// Defaulted, not asserted: a read served by a deployment that predates `names` returns
	// the field absent, and a naming card is not worth taking the whole page down for. The
	// card then reports its gap, which is the truthful thing to say about an answer that
	// carried no names.
	names = [],
	emptyLabel = 'No market names are recorded for this product.',
	subject = 'product',
	defaultName,
	isLoading,
	isError,
}: ProductNamingCardProps) {
	return (
		<SectionCard title={PRODUCT_NAMING_TITLE} count={isLoading || isError ? undefined : names.length}>
			{isLoading ? (
				<ProductNamingBodySkeleton label={`Loading the ${subject}'s names…`} />
			) : isError ? (
				<p className="text-sm text-ds-text-muted">{`Could not load this ${subject}'s names. Please retry.`}</p>
			) : names.length === 0 ? (
				<p className="text-sm text-ds-text-muted">{emptyLabel}</p>
			) : (
				<ul>
					{/* The baseline the overrides differ FROM. Labelled "Everywhere else" rather than
					    "Default", because that is what it means to a reader: the name in force in every
					    market no row below claims. */}
					{defaultName ? (
						<li key="__default__" className={ROW_CLASS}>
							<span className={SCOPE_CLASS}>Everywhere else</span>
							<span className="min-w-0 flex-1 text-sm text-ds-text">{defaultName}</span>
						</li>
					) : null}
					{names.map((entry) => (
						// One row per scope: `value_mapping` is unique on (value, system, geography).
						<li key={entry.geoCode} className={ROW_CLASS}>
							<span className={SCOPE_CLASS}>{entry.geoLabel}</span>
							<span className="min-w-0 flex-1 text-sm text-ds-text">{entry.name}</span>
						</li>
					))}
				</ul>
			)}
		</SectionCard>
	)
}
